import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip as ChartTooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import { CROSSPLOT_CLASSES, crossplotData, pointsOfClass } from '../lib/crossplot';

const fmt = (v) => Number(v).toPrecision(4);

/** Calculated vs measured apparent resistivity (log-log, 1:1 line, sign-coded) of a data-fit block. */
export default function Crossplot({ C, fit }) {
  const data = useMemo(() => crossplotData(fit), [fit]);
  if (!data.range) {
    return (
      <div data-testid="crossplot-empty" style={{ fontSize: 11, color: C.text3, padding: '18px 10px', textAlign: 'center', background: C.bg3, borderRadius: 8 }}>
        No measured / calculated data stored with this model.<br />
        Import an EarthImager .out file or a BERT data_fit.csv to see the crossplot.
      </div>
    );
  }
  const [lo, hi] = data.range;
  const present = CROSSPLOT_CLASSES.filter((c) => pointsOfClass(data.points, c.id).length);
  return (
    <div data-testid="crossplot">
      <div style={{ fontSize: 12, fontWeight: 600, color: C.text0, marginBottom: 6 }}>
        Calculated vs measured apparent resistivity
        {fit.iteration != null ? ` (iteration ${fit.iteration})` : ''} · RMS {data.rms.toFixed(2)}%
      </div>
      <div style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 16, bottom: 22, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis type="number" dataKey="x" name="Measured" scale="log" domain={[lo, hi]} allowDataOverflow tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border}
              label={{ value: 'Measured apparent resistivity (ohm.m)', position: 'insideBottom', offset: -10, fontSize: 10, fill: C.text3 }} />
            <YAxis type="number" dataKey="y" name="Calculated" scale="log" domain={[lo, hi]} allowDataOverflow tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border}
              label={{ value: 'Calculated (ohm.m)', angle: -90, position: 'insideLeft', fontSize: 10, fill: C.text3 }} />
            <ZAxis range={[28, 28]} />
            <ChartTooltip cursor={{ strokeDasharray: '3 3' }}
              content={({ active, payload }) => {
                const p = active && payload?.[0]?.payload;
                if (!p) return null;
                return (
                  <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.text1, padding: '6px 8px' }}>
                    <div>Reading {p.seq}</div>
                    <div>Measured: {fmt(p.measured)} ohm.m</div>
                    <div>Calculated: {fmt(p.calculated)} ohm.m</div>
                    <div>Misfit: {p.misfit.toFixed(1)}%</div>
                  </div>
                );
              }} />
            <ReferenceLine segment={[{ x: lo, y: lo }, { x: hi, y: hi }]} stroke={C.text2} strokeDasharray="6 4" ifOverflow="extendDomain" />
            {CROSSPLOT_CLASSES.map((c) => (
              <Scatter key={c.id} name={c.label} data={pointsOfClass(data.points, c.id)} fill={c.color} stroke="rgba(255,255,255,0.45)" isAnimationActive={false} />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div data-testid="crossplot-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 10, color: C.text2, marginTop: 4 }}>
        {present.map((c) => (
          <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: 99, background: c.color, border: '1px solid rgba(255,255,255,0.45)' }} />
            {c.label} ({pointsOfClass(data.points, c.id).length})
          </span>
        ))}
        <span>Dashed line: 1:1. Negative values are plotted by magnitude.</span>
      </div>
      {data.dropped > 0 && (
        <div style={{ fontSize: 9, color: C.text3, marginTop: 4 }}>{data.dropped} readings with a zero or missing value are not drawn.</div>
      )}
    </div>
  );
}
